import { Component } from "react";
import { Navigate } from "react-router";
import { withTranslation } from "react-i18next";
import {
	Card,
	CardHeader,
	Paper,
	Select,
	MenuItem,
	ListItemText,
	CircularProgress,
} from "@mui/material";
// cmp
import ImportTable from "./ImportTable";
import TableEditor from "./TableEditor";
import Svg from "../svg";
import withNavigateAndParams from "../withNavigateAndParams";
// services
import User from "../../services/user";
import Gupport from "../../services/gupport";
import { sortAlphabetically } from "../../services/l10n";
import { Storage, StorageKeys } from "../../services/storage";
// types
import type { WithTranslation } from "react-i18next";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { ReactRouterProps } from "../../types/misc";
import type { CmdGetTableNames, TableData, TableNameId } from "../../types/gupport";

type RouteParams = {
	tableId: TableNameId;
};

type Props = Readonly<WithTranslation & ReactRouterProps<RouteParams>>;

type State = {
	loading: boolean;
	tables: ReadonlyArray<TableData>;
	selectedTable: TableNameId | null;
	error: any;
};

class TEditorSelector extends Component<Props, State> {

	constructor(props: Props) {
		super(props);

		let selectedTable: TableNameId | null;
		if (this.props.params.tableId) {
			Storage.set(StorageKeys.teditorTable, this.props.params.tableId);
			selectedTable = this.props.params.tableId;
		} else if (Storage.get(StorageKeys.teditorTable)) {
			selectedTable = Storage.get(StorageKeys.teditorTable);
		} else {
			selectedTable = null;
		}

		this.state = {
			loading: true,
			tables: [],
			selectedTable: selectedTable,
			error: null,
		};

		this.handleTableChange = this.handleTableChange.bind(this);
	}

	override componentDidMount() {
		this.fetchTables();
	}

	override componentDidUpdate(prevProps) {
		if (this.props.params.tableId && this.props.params.tableId !== prevProps.params.tableId) {
			Storage.set(StorageKeys.teditorTable, this.props.params.tableId);
			this.setState({
				selectedTable: this.props.params.tableId,
			});
		}
	}

	fetchTables() {
		const cmd = {
			action: "getTableNames",
		} as const satisfies CmdGetTableNames;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				const tables = msg.payload.data
					.filter((table) => (User.user?.table_read.includes(table.id)))
					.toSorted((a, b) => (sortAlphabetically(a.desc, b.desc)));

				this.setState({
					loading: false,
					tables: tables,
					error: null,
				});
				if (tables.length > 0 && this.state.selectedTable === null) {
					void this.props.navigate(`/teditor/${tables[0].id}`);
				}
			} else {
				this.setState({
					loading: false,
					error: error ?? msg?.payload.data,
				});
			}
		});
	}

	handleTableChange(event: SelectChangeEvent<TableNameId>) {
		void this.props.navigate(`/teditor/${event.target.value}`);
	}

	override render() {
		const { t } = this.props;

		if (this.props.params.tableId === undefined && this.state.selectedTable !== null) {
			return <Navigate to={`/teditor/${this.state.selectedTable}`} />;
		}

		if (this.state.loading) {
			return <CircularProgress />;
		}

		const style = {
			marginTop: "16px",
			fontWeight: "bold",
			padding: "16px",
		};
		if (this.state.error) {
			return <div style={style}>{t("generic.error")}</div>;
		}

		const hasWriteAccess = User.user?.table_write.includes(this.state.selectedTable) ?? false;

		return (
			<>
				<Paper style={{ display: "flex", alignItems: "center", padding: "12px" }}>
					<div style={{ flexGrow: 1, margin: "0 16px", fontWeight: "bold" }}>{t("teditor.table")}</div>
					<Select
						value={this.state.selectedTable}
						onChange={this.handleTableChange}
						style={{ flexGrow: 1, margin: "0 16px" }}
					>
						{
							this.state.tables.map((table) => (
								<MenuItem key={table.id} value={table.id}>
									<ListItemText primary={table.desc} />
								</MenuItem>
							))
						}
					</Select>
					{Storage.get(StorageKeys.expertMode) && <ImportTable tables={this.state.tables} />}
				</Paper>
				{(this.state.tables.length === 0)
					? <div style={style}>{t("teditor.noTables")}</div>
					: this.state.tables.some((table) => (table.id === this.state.selectedTable))
						?
						<>
							{!hasWriteAccess &&
								<Card sx={(theme) => ({ marginTop: "12px", color: theme.vars.palette.text.primary, backgroundColor: theme.vars.palette.primary.main })}>
									<CardHeader
										title={t("customTableEditor.errors.rbac_not_authorized")}
										avatar={<Svg src="messages/error.svg" />}
									/>
								</Card>
							}
							<TableEditor tableId={this.state.selectedTable} hasWriteAccess={hasWriteAccess} />
						</>
						: <div style={style}>{t("teditor.noSelectedTable")}</div>
				}
			</>
		);
	}

}

export default withTranslation()(withNavigateAndParams(TEditorSelector));
